import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp, openExternalUrl } from '@/lib/telegram';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { IconChip, SectionLabel } from '@/components/ui/calm';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage, SkeletonParagraph } from '@/components/ui/skeleton';
import {
  Infinity as InfinityIcon,
  Smartphone,
  Zap,
  Globe2,
  ShieldCheck,
  EyeOff,
  CreditCard,
  type LucideIcon,
} from 'lucide-react';

export const Route = createFileRoute('/faq')({
  component: FaqPage,
});

// Маппинг icon-ключей бэка (/api/client/info → features[].icon) на lucide-компоненты.
const FEATURE_ICONS: Record<string, LucideIcon> = {
  infinity: InfinityIcon,
  devices: Smartphone,
  instant: Zap,
  servers: Globe2,
  bypass: ShieldCheck,
  no_logs: EyeOff,
};

/** Чистим возможные HTML-теги/лишние пробелы из about_text (бэк/партнёр мог
 *  оставить <b>…</b> и т.п.) — показываем как обычный абзац. */
function cleanText(raw: string | undefined): string {
  if (!raw) return '';
  return raw.replace(/<[^>]+>/g, '').replace(/[ \t]+\n/g, '\n').trim();
}

function FaqPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending } = useQuery({ queryKey: ['client-info'], queryFn: () => api.info() });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  useEffect(() => {
    const tg = getWebApp();
    tg?.MainButton.hide();
  }, []);

  const about = cleanText(data?.about_text);
  // Тот же текст, что в боте на вкладке «FAQ» раздела «О сервисе». Без него кнопка «О
  // сервисе», открытая в мини-аппе (заявка #181), теряла бы ответы партнёра.
  const faq = cleanText(data?.faq_text);
  const features = data?.features ?? [];

  if (isPending) return <FaqSkeleton />;

  return (
    <div className="p-4 space-y-5 pb-24">
      <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] px-2 pt-2 text-foreground">FAQ</h2>

      <Card>
        <CardContent className="p-5 space-y-2">
          <h3 className="font-bold text-base text-foreground">{t('FAQ what is it')}</h3>
          <p className="text-sm text-[var(--t-body)] leading-relaxed whitespace-pre-wrap">
            {about || t('FAQ about fallback')}
          </p>
        </CardContent>
      </Card>

      {faq && (
        <Card>
          <CardContent className="p-5 space-y-2">
            <h3 className="font-bold text-base text-foreground">{t('FAQ questions')}</h3>
            <p className="text-sm text-[var(--t-body)] leading-relaxed whitespace-pre-wrap">{faq}</p>
          </CardContent>
        </Card>
      )}

      {features.length > 0 && (
        <div>
          <SectionLabel className="px-2 mb-2">{t('FAQ whats inside')}</SectionLabel>
          <Card>
            <CardContent className="p-2 divide-y divide-[var(--t-line)]">
              {features.map((f) => {
                const Icon = FEATURE_ICONS[f.icon] ?? ShieldCheck;
                return (
                  <div key={f.icon} className="flex items-center gap-3 p-3">
                    <IconChip icon={Icon} tone="blue" size={40} />
                    <span className="text-sm font-medium text-foreground">{f.text}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="rounded-[var(--r-card)] calm-card p-4 flex items-start gap-3">
        <IconChip icon={CreditCard} tone="neutral" size={40} />
        <div className="text-sm text-[var(--t-muted)] leading-snug">
          {t('FAQ payment note')}
        </div>
      </div>

      {/* Документы (#208): те же ссылки, что в боте и в подвале сайта. */}
      {data?.terms_url || data?.privacy_url ? (
        <nav aria-label={t('Documents')} className="flex flex-col items-center gap-1 pt-1">
          {[
            { url: data?.terms_url, label: t('Terms of service') },
            { url: data?.privacy_url, label: t('Privacy policy') },
          ]
            .filter((d) => d.url)
            .map((d) => (
              <button
                key={d.label}
                type="button"
                onClick={() => openExternalUrl(d.url)}
                className="min-h-11 px-3 text-sm text-[var(--t-muted)] underline decoration-white/20 underline-offset-4 active:opacity-70"
              >
                {d.label}
              </button>
            ))}
        </nav>
      ) : null}
    </div>
  );
}

/** Описание бота партнёра и список его фич — оба приходят из /info. */
function FaqSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-5">
      <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] px-2 pt-2 text-foreground">FAQ</h2>
      <SkeletonCard className="rounded-[var(--r-card)] min-h-0 p-5 space-y-3">
        <SkeletonLine size="lg" className="w-2/5" />
        <SkeletonParagraph lines={4} size="sm" />
      </SkeletonCard>
      <div>
        <SectionLabel className="px-2 mb-2">{t('FAQ whats inside')}</SectionLabel>
        <SkeletonCard className="rounded-[var(--r-card)] min-h-0 p-2 space-y-1">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
              <SkeletonLine className="w-1/2" />
            </div>
          ))}
        </SkeletonCard>
      </div>
    </SkeletonPage>
  );
}
